import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiLink, FiLink2, FiCheckCircle, FiXCircle, FiEdit2, FiTrash2, FiRefreshCw, FiX, FiUpload, FiExternalLink, FiShield } from 'react-icons/fi';
import { signatureIntegrationsAPI } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader/PageHeader';
import './SignatureIntegrations.scss';

const DOCUSIGN_LOGO = (
  <svg viewBox="0 0 200 200" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="200" rx="24" fill="#4C00FF" />
    <path d="M60 140V60h20v60h40V60h20v80H60Z" fill="#fff" />
  </svg>
);

const EMPTY_FORM = {
  integrationKey: '',
  secretKey: '',
  rsaPrivateKey: '',
  accountId: '',
  userId: '',
  environment: 'sandbox',
  label: 'DocuSign',
};

function SignatureIntegrations() {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('connect'); // 'connect' | 'manage' | 'edit'
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [consentInfo, setConsentInfo] = useState(null); // { consentUrl, formSnapshot }
  // SEC-25: the API generates the webhook HMAC secret server-side and returns
  // it exactly once; afterwards every response masks it. Hold it here until the
  // user has copied it into DocuSign Connect.
  const [hmacReveal, setHmacReveal] = useState(null);
  const [hmacCopied, setHmacCopied] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await signatureIntegrationsAPI.list();
      setIntegrations(res.data.integrations);
    } catch (err) {
      showToast(t('signatureIntegrations.messages.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleConnect = (integration) => {
    setSelectedIntegration(integration);
    setForm({ ...EMPTY_FORM });
    setModalMode('connect');
    setModalOpen(true);
    setDisconnectConfirm(false);
  };

  const handleManage = async (integration) => {
    try {
      const res = await signatureIntegrationsAPI.get(integration._id);
      setSelectedIntegration(res.data.integration);
      setModalMode('manage');
      setModalOpen(true);
      setDisconnectConfirm(false);
    } catch {
      showToast(t('signatureIntegrations.messages.failedToLoadDetails'), 'error');
    }
  };

  const handleEdit = () => {
    setForm({
      integrationKey: selectedIntegration.config?.integrationKey || '',
      secretKey: selectedIntegration.config?.secretKey || '',
      rsaPrivateKey: selectedIntegration.config?.rsaPrivateKey || '',
      accountId: selectedIntegration.config?.accountId || '',
      userId: selectedIntegration.config?.userId || '',
      environment: selectedIntegration.config?.environment || 'sandbox',
      label: selectedIntegration.label || 'DocuSign',
    });
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIntegration(null);
    setForm({ ...EMPTY_FORM });
    setDisconnectConfirm(false);
    setConsentInfo(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((prev) => ({ ...prev, rsaPrivateKey: ev.target.result }));
    };
    reader.readAsText(file);
  };

  const handleSave = async () => {
    if (!form.integrationKey || !form.secretKey || !form.rsaPrivateKey || !form.accountId || !form.userId) {
      showToast(t('signatureIntegrations.messages.allFieldsRequired'), 'error');
      return;
    }
    try {
      setSaving(true);
      let res;
      if (modalMode === 'edit' && selectedIntegration?._id) {
        res = await signatureIntegrationsAPI.update(selectedIntegration._id, {
          ...form,
          provider: 'docusign',
        });
        showToast(t('signatureIntegrations.messages.integrationUpdated'));
      } else {
        res = await signatureIntegrationsAPI.connect({
          ...form,
          provider: 'docusign',
        });
        showToast(t('signatureIntegrations.messages.connectedSuccessfully'));
      }
      handleCloseModal();
      fetchIntegrations();
      if (res?.data?.hmacSecretGeneratedOnce && res.data.hmacSecret) {
        setHmacCopied(false);
        setHmacReveal(res.data.hmacSecret);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.consent_required) {
        setConsentInfo({ consentUrl: data.consentUrl, formSnapshot: { ...form } });
        return;
      }
      const msg = data?.error || t('signatureIntegrations.messages.failedToSave');
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedIntegration?._id) return;
    try {
      setTesting(true);
      const res = await signatureIntegrationsAPI.test(selectedIntegration._id);
      showToast(res.data.message || t('signatureIntegrations.messages.connectionSuccessful'));
      // Refresh the manage modal data
      const updated = await signatureIntegrationsAPI.get(selectedIntegration._id);
      setSelectedIntegration(updated.data.integration);
      fetchIntegrations();
    } catch (err) {
      const data = err.response?.data;
      if (data?.consent_required) {
        setConsentInfo({ consentUrl: data.consentUrl });
        return;
      }
      const msg = data?.error || t('signatureIntegrations.messages.connectionTestFailed');
      showToast(msg, 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!selectedIntegration?._id) return;
    try {
      setSaving(true);
      await signatureIntegrationsAPI.disconnect(selectedIntegration._id);
      showToast(t('signatureIntegrations.messages.disconnected'));
      handleCloseModal();
      fetchIntegrations();
    } catch (err) {
      showToast(t('signatureIntegrations.messages.failedToDisconnect'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => formatDateTime(d);

  return (
    <div className="sig-integrations">
      {/* Toast */}
      {toast && (
        <div className={`sig-toast sig-toast--${toast.type}`}>
          {toast.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
          <span>{toast.message}</span>
          <button className="sig-toast__close" onClick={() => setToast(null)}><FiX /></button>
        </div>
      )}

      {/* SEC-25: one-time reveal of the generated webhook HMAC secret */}
      {hmacReveal && (
        <div className="sig-hmac" role="dialog" aria-modal="true" aria-labelledby="sig-hmac-title">
          <div className="sig-hmac__card">
            <h3 id="sig-hmac-title">{t('signatureIntegrations.hmacReveal.title')}</h3>
            <p>{t('signatureIntegrations.hmacReveal.desc')}</p>
            <code className="sig-hmac__secret">{hmacReveal}</code>
            <div className="sig-hmac__actions">
              <button
                type="button"
                className="sig-btn sig-btn--primary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(hmacReveal);
                    setHmacCopied(true);
                  } catch {
                    /* clipboard blocked — the value stays visible for manual copy */
                  }
                }}
              >
                {hmacCopied ? t('signatureIntegrations.hmacReveal.copied') : t('signatureIntegrations.hmacReveal.copy')}
              </button>
              <button
                type="button"
                className="sig-btn sig-btn--secondary"
                onClick={() => { setHmacReveal(null); setHmacCopied(false); }}
              >
                {t('signatureIntegrations.hmacReveal.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <PageHeader
        label={t('nav.accessCompliance', 'Access & Compliance')}
        labelIcon={<FiShield />}
        title={t('signatureIntegrations.title')}
        description={t('signatureIntegrations.subtitle')}
      />

      {/* Integration Cards Grid */}
      {loading ? (
        <div className="sig-integrations__loading">
          <div className="sig-integrations__spinner" />
        </div>
      ) : (
        <div className="sig-integrations__grid">
          {integrations.map((integration, idx) => (
            <div key={integration._id || idx} className="sig-card">
              <div className="sig-card__header">
                <div className="sig-card__logo">{DOCUSIGN_LOGO}</div>
                <span className={`sig-card__badge sig-card__badge--${integration.status === 'linked' ? 'linked' : 'not-linked'}`}>
                  {integration.status === 'linked' ? t('signatureIntegrations.linked') : t('signatureIntegrations.notLinked')}
                </span>
              </div>
              <div className="sig-card__body">
                <h3 className="sig-card__name">{integration.label || 'DocuSign'}</h3>
                <p className="sig-card__desc">
                  {t('signatureIntegrations.docusignDescription')}
                </p>
              </div>
              <div className="sig-card__footer">
                {integration.status === 'linked' ? (
                  <button className="sig-btn sig-btn--secondary" onClick={() => handleManage(integration)}>
                    <FiLink2 /> {t('signatureIntegrations.manage')}
                  </button>
                ) : (
                  <button className="sig-btn sig-btn--primary" onClick={() => handleConnect(integration)}>
                    <FiLink /> {t('signatureIntegrations.connect')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Overlay */}
      {modalOpen && (
        <div className="sig-modal-overlay" onClick={handleCloseModal}>
          <div className="sig-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sig-modal__header">
              <h2 className="sig-modal__title">
                {modalMode === 'manage' ? t('signatureIntegrations.manageDocuSign') : modalMode === 'edit' ? t('signatureIntegrations.editDocuSignConfig') : t('signatureIntegrations.connectDocuSign')}
              </h2>
              <button className="sig-modal__close" onClick={handleCloseModal}><FiX /></button>
            </div>

            <div className="sig-modal__body">
              {/* ─── Manage View ─── */}
              {modalMode === 'manage' && selectedIntegration && (
                <div className="sig-manage">
                  <div className="sig-manage__status">
                    <span className={`sig-card__badge sig-card__badge--${selectedIntegration.status === 'linked' ? 'linked' : 'not-linked'}`}>
                      {selectedIntegration.status === 'linked' ? t('signatureIntegrations.linked') : t('signatureIntegrations.notLinked')}
                    </span>
                  </div>

                  <div className="sig-manage__meta">
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.environment')}</span>
                      <span className="sig-manage__meta-value">
                        {selectedIntegration.config?.environment === 'production' ? t('signatureIntegrations.production') : t('signatureIntegrations.sandbox')}
                      </span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.connectedOn')}</span>
                      <span className="sig-manage__meta-value">{formatDate(selectedIntegration.connectedAt)}</span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.connectedBy')}</span>
                      <span className="sig-manage__meta-value">
                        {selectedIntegration.connectedBy
                          ? `${selectedIntegration.connectedBy.firstName || ''} ${selectedIntegration.connectedBy.lastName || ''}`.trim() ||
                            selectedIntegration.connectedBy.email
                          : '—'}
                      </span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.lastVerified')}</span>
                      <span className="sig-manage__meta-value">{formatDate(selectedIntegration.lastVerifiedAt)}</span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.integrationKey')}</span>
                      <span className="sig-manage__meta-value sig-manage__meta-value--mono">
                        {selectedIntegration.config?.integrationKey || '—'}
                      </span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.accountId')}</span>
                      <span className="sig-manage__meta-value sig-manage__meta-value--mono">
                        {selectedIntegration.config?.accountId || '—'}
                      </span>
                    </div>
                    <div className="sig-manage__meta-item">
                      <span className="sig-manage__meta-label">{t('signatureIntegrations.userId')}</span>
                      <span className="sig-manage__meta-value sig-manage__meta-value--mono">
                        {selectedIntegration.config?.userId || '—'}
                      </span>
                    </div>
                    {selectedIntegration.config?.webhookUrl && (
                      <div className="sig-manage__meta-item">
                        <span className="sig-manage__meta-label">{t('signatureIntegrations.webhookUrl')}</span>
                        <span className="sig-manage__meta-value sig-manage__meta-value--mono sig-manage__meta-value--small">
                          {selectedIntegration.config.webhookUrl}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="sig-manage__actions">
                    <button className="sig-btn sig-btn--secondary" onClick={handleEdit}>
                      <FiEdit2 /> {t('signatureIntegrations.editConfiguration')}
                    </button>
                    <button className="sig-btn sig-btn--secondary" onClick={handleTestConnection} disabled={testing}>
                      <FiRefreshCw className={testing ? 'spin' : ''} />
                      {testing ? t('signatureIntegrations.testing') : t('signatureIntegrations.testConnection')}
                    </button>
                    {!disconnectConfirm ? (
                      <button className="sig-btn sig-btn--danger-outline" onClick={() => setDisconnectConfirm(true)}>
                        <FiTrash2 /> {t('signatureIntegrations.disconnect')}
                      </button>
                    ) : (
                      <div className="sig-manage__confirm">
                        <span>{t('signatureIntegrations.confirmDisconnect')}</span>
                        <button className="sig-btn sig-btn--danger" onClick={handleDisconnect} disabled={saving}>
                          {saving ? t('signatureIntegrations.disconnecting') : t('signatureIntegrations.yesDisconnect')}
                        </button>
                        <button className="sig-btn sig-btn--ghost" onClick={() => setDisconnectConfirm(false)}>
                          {t('signatureIntegrations.cancel')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ─── Connect / Edit Form ─── */}
              {(modalMode === 'connect' || modalMode === 'edit') && (
                <div className="sig-form">
                  <div className="sig-form__prereq">
                    <div className="sig-form__prereq-icon"><FiExternalLink /></div>
                    <div className="sig-form__prereq-content">
                      <strong>{t('signatureIntegrations.beforeYouConnect')}</strong> {t('signatureIntegrations.redirectUriInstructions')}
                      <code className="sig-form__prereq-uri">
                        https://developers.docusign.com/platform/auth/consent
                      </code>
                      {t('signatureIntegrations.consentFlowRequired')}
                    </div>
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">{t('signatureIntegrations.labelDisplayName')}</label>
                    <input
                      className="sig-form__input"
                      value={form.label}
                      onChange={(e) => handleFormChange('label', e.target.value)}
                      placeholder="DocuSign"
                    />
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.integrationKeyClientId')} <span className="sig-form__required">*</span>
                    </label>
                    <input
                      className="sig-form__input"
                      value={form.integrationKey}
                      onChange={(e) => handleFormChange('integrationKey', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.secretKeyClientSecret')} <span className="sig-form__required">*</span>
                    </label>
                    <input
                      className="sig-form__input"
                      type="password"
                      value={form.secretKey}
                      onChange={(e) => handleFormChange('secretKey', e.target.value)}
                      placeholder={t('signatureIntegrations.enterSecretKey')}
                    />
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.rsaPrivateKey')} <span className="sig-form__required">*</span>
                    </label>
                    <textarea
                      className="sig-form__textarea"
                      value={form.rsaPrivateKey}
                      onChange={(e) => handleFormChange('rsaPrivateKey', e.target.value)}
                      placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                      rows={5}
                    />
                    <label className="sig-form__file-upload">
                      <FiUpload /> {t('signatureIntegrations.uploadPemFile')}
                      <input type="file" accept=".pem,.key,.txt" onChange={handleFileUpload} hidden />
                    </label>
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.docusignAccountId')} <span className="sig-form__required">*</span>
                    </label>
                    <input
                      className="sig-form__input"
                      value={form.accountId}
                      onChange={(e) => handleFormChange('accountId', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.userIdApiUsername')} <span className="sig-form__required">*</span>
                    </label>
                    <input
                      className="sig-form__input"
                      value={form.userId}
                      onChange={(e) => handleFormChange('userId', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                  </div>

                  <div className="sig-form__field">
                    <label className="sig-form__label">
                      {t('signatureIntegrations.environment')} <span className="sig-form__required">*</span>
                    </label>
                    <select
                      className="sig-form__select"
                      value={form.environment}
                      onChange={(e) => handleFormChange('environment', e.target.value)}
                    >
                      <option value="sandbox">{t('signatureIntegrations.sandboxOption')}</option>
                      <option value="production">{t('signatureIntegrations.productionOption')}</option>
                    </select>
                  </div>
                </div>
              )}

              {/* ─── Consent Required Banner ─── */}
              {consentInfo && (
                <div className="sig-consent">
                  <div className="sig-consent__icon">
                    <FiExternalLink />
                  </div>
                  <div className="sig-consent__content">
                    <h4 className="sig-consent__title">{t('signatureIntegrations.userConsentRequired')}</h4>
                    <p className="sig-consent__text">
                      {t('signatureIntegrations.consentDescription')}
                    </p>
                    <div className="sig-consent__actions">
                      <a
                        className="sig-btn sig-btn--primary"
                        href={consentInfo.consentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiExternalLink /> {t('signatureIntegrations.grantConsent')}
                      </a>
                      <button
                        className="sig-btn sig-btn--secondary"
                        onClick={() => {
                          setConsentInfo(null);
                          if (consentInfo.formSnapshot) {
                            setForm(consentInfo.formSnapshot);
                          }
                          handleSave();
                        }}
                        disabled={saving}
                      >
                        <FiRefreshCw /> {saving ? t('signatureIntegrations.retrying') : t('signatureIntegrations.retryConnection')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {(modalMode === 'connect' || modalMode === 'edit') && !consentInfo && (
              <div className="sig-modal__footer">
                <button className="sig-btn sig-btn--ghost" onClick={handleCloseModal}>
                  {t('signatureIntegrations.cancel')}
                </button>
                <button className="sig-btn sig-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('signatureIntegrations.saving') : t('signatureIntegrations.saveAndConnect')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SignatureIntegrations;
